import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload, Video as VideoIcon, Check, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const UploadVideoPage = ({ user }) => {
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const file = e.dataTransfer.files[0];
    if (file && file.type.startsWith('video/')) {
      setSelectedFile(file);
    } else {
      toast.error('Please select a video file');
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // 25MB limit for fast, reliable uploads
      if (file.size > 25 * 1024 * 1024) {
        toast.error('File too large. Maximum size: 25MB (about 30 seconds of video)');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) return;

    setUploading(true);
    setUploadProgress(0);
    const formData = new FormData();
    formData.append('file', selectedFile);

    try {
      const token = localStorage.getItem('kaplie_token');
      const response = await axios.post(`${API}/videos/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        timeout: 120000, // 2 minute timeout for upload
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      toast.success('Video uploaded! AI is generating captions...');
      setTimeout(() => {
        navigate('/my-videos');
      }, 1500);
    } catch (error) {
      console.error('Upload error:', error);
      if (error.code === 'ECONNABORTED') {
        toast.error('Upload timed out. Try a smaller video.');
      } else if (error.response?.status === 413) {
        toast.error('File too large for server. Try a smaller video.');
      } else {
        toast.error(error.response?.data?.detail || 'Upload failed. Please try again.');
      }
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    }
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="min-h-screen bg-black/40 text-white">
      {/* Navigation */}
      <nav className="border-b border-white/[0.08] bg-white/[0.03]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold tracking-tighter">Kaplie</div>
          <Link to="/dashboard" data-testid="back-to-dashboard-link">
            <button className="flex items-center gap-2 text-[#A1A1AA] hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
              Back to Dashboard
            </button>
          </Link>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-3xl mx-auto px-6 py-20">
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold tracking-tighter mb-4" data-testid="upload-heading">
            Upload Video
          </h1>
          <p className="text-lg text-[#A1A1AA]">
            Upload your video and AI will automatically generate captions
          </p>
        </div>

        {/* Upload Area */}
        <div className="bg-white/[0.03] border border-white/[0.08] rounded-2xl p-8 shadow-sm shadow-black/50">
          {!selectedFile ? (
            <div
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`border-2 border-dashed rounded-2xl p-12 text-center transition-all ${
                dragActive
                  ? 'border-[#D4AF37]/50 bg-[#D4AF37]/[0.05]'
                  : 'border-white/[0.08] hover:border-[#D4AF37]/30'
              }`}
              data-testid="upload-dropzone"
            >
              <div className="flex flex-col items-center gap-4">
                <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center">
                  <Upload className="w-8 h-8 text-[#D4AF37]" />
                </div>
                <div>
                  <p className="text-xl font-bold mb-2">Drag & drop your video here</p>
                  <p className="text-[#A1A1AA] mb-4">or</p>
                  <label htmlFor="file-upload">
                    <div className="inline-flex items-center gap-2 px-6 py-3 font-bold bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all cursor-pointer">
                      <VideoIcon className="w-5 h-5" />
                      Browse Files
                    </div>
                  </label>
                  <input
                    id="file-upload"
                    type="file"
                    accept="video/*"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                </div>
                <p className="text-sm text-[#71717A] mt-4">
                  Supported formats: MP4, MOV, AVI, WebM • Max size: 25MB (~30 sec)
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-6" data-testid="file-selected">
              {/* File Info */}
              <div className="flex items-start gap-4 bg-black/40 border border-white/[0.08] rounded-xl p-6">
                <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                  <VideoIcon className="w-6 h-6 text-black" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-bold text-lg truncate">{selectedFile.name}</p>
                  <p className="text-[#A1A1AA] text-sm">{formatFileSize(selectedFile.size)}</p>
                </div>
                {!uploading && (
                  <button
                    onClick={() => setSelectedFile(null)}
                    className="text-[#A1A1AA] hover:text-white transition-colors"
                  >
                    Remove
                  </button>
                )}
              </div>

              {/* Upload Progress */}
              {uploading && (
                <div className="space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#A1A1AA]">
                      {uploadProgress < 100 ? 'Uploading...' : 'Processing...'}
                    </span>
                    <span className="font-bold text-[#D4AF37]">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-white/[0.06] rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-[#D4AF37] h-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-[#71717A] text-center">
                    {uploadProgress < 100 
                      ? 'Please wait while your video uploads...' 
                      : 'Upload complete! Starting caption generation...'}
                  </p>
                </div>
              )}

              {/* Upload Button */}
              {!uploading && (
                <button
                  onClick={handleUpload}
                  data-testid="upload-button"
                  className="w-full px-6 py-4 font-bold bg-gradient-to-r from-[#D4AF37] to-[#C5A059] text-black rounded-full hover:shadow-[0_0_30px_rgba(212,175,55,0.35)] transition-all"
                >
                  Upload & Generate Captions
                </button>
              )}

              {/* Info Box */}
              <div className="flex items-start gap-3 bg-[#D4AF37]/5 border border-[#D4AF37]/20 rounded-xl p-4">
                <AlertCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-[#D4AF37] mb-1">AI Caption Generation</p>
                  <p className="text-[#A1A1AA]">
                    Your video will be processed and captions will be generated automatically. This may take a few moments depending on video length.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UploadVideoPage;
